import { Helmet } from 'react-helmet'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { HashLink as Link } from 'react-router-hash-link'
import { motion, useAnimationControls } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MainHeader from '../../../components/MainHeader'
import MainFooter from '../../../components/MainFooter'
import CTAButton from '../../../components/CTAButton'
import AnimatedQuote from '../../../components/AnimatedQuote'
import PricingCard, { BundleCard } from '../../../components/PricingCard'
import { boxvar1, boxvar3, boxvar2_1, boxvar2_2, boxvar2dyndelay, oPU } from '../../../core.js'
import { faPhone, faChevronDown, faMagnifyingGlassArrowRight, faTags } from '@fortawesome/free-solid-svg-icons'
import packageJson from '../../../../package.json'

import testimonial_klaus from '../../../assets/pictures/testimonials/testimonial_klaus.webp'
import macbook_webdev from '../../../assets/pictures/sectionimages/macbook_webdev-cut_compressed.webp'

import '../../Pricing/Pricing.css'
import '../../Pricing/MPricing.css'
import '../../Home/Home.css'
import '../../Home/MHome.css'
import './FunnelKMU.css'
import './MFunnelKMU.css'
import useProgressiveHeroBg from '../../../hooks/useProgressiveHeroBg'

const scrolldowniconanim = {
    idle: {
        y: [0, 6, 0],
        scale: 1,
        transition: {
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeOut',
        },
    },
    hover: {
        y: [0, 3, 0],
        scale: 1.2,
        transition: {
            duration: 1.0,
            repeat: Infinity,
            ease: 'easeInOut',
        },
    },
}

const CALENDLY = 'https://calendly.com/tonetworkde-meet/getintouch'
const STAND = packageJson.pricingdate
const SITE = 'https://tonetwork.de'

const REGIONS = {
    memmingen: {
        city: 'Memmingen',
        region: 'Unterallgäu',
        canonical: '/landing/webdesign-memmingen',
        seoTitle: 'Webdesign Memmingen – Website & Betreuung für KMU | ToNetwork',
        seoDescription: 'Webdesign in Memmingen für KMU: professionelle Website, Business-Fotos und laufende Betreuung ab 2.490 €. Persönlicher Partner aus dem Unterallgäu – Listenpreise, individuelles Angebot.',
        h1: 'Webdesign in Memmingen',
        h1sub: 'Website, Fotos & Betreuung für KMU im Unterallgäu – aus einer Hand.',
        localLead: 'Wir sitzen in Hawangen bei Memmingen und betreuen Unternehmen in Memmingen, im Unterallgäu und im Allgäu – persönlich, pragmatisch und langfristig.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Hawangen', 'Babenhausen', 'Türkheim'],
    },
    unterallgaeu: {
        city: 'Unterallgäu',
        region: 'Allgäu',
        canonical: '/landing/webdesign-unterallgaeu',
        seoTitle: 'Webdesign Unterallgäu – Website für KMU ab 2.490 € | ToNetwork',
        seoDescription: 'Webdesign im Unterallgäu für kleine und mittlere Unternehmen: individuelle Website, Teamfotos und Care-Betreuung. Partner aus Hawangen – transparente Preise, kostenloses Erstgespräch.',
        h1: 'Webdesign im Unterallgäu',
        h1sub: 'Dein KMU verdient einen Online-Auftritt, der Kunden gewinnt – nicht nur existiert.',
        localLead: 'Vom Unterallgäu bis ins Allgäu: Wir sind dein fester Ansprechpartner für Webentwicklung, Hosting und Media – ohne Agentur-Overhead.',
        areaServed: ['Unterallgäu', 'Memmingen', 'Türkheim', 'Mindelheim', 'Bad Wörishofen'],
    },
    allgaeu: {
        city: 'Allgäu',
        region: 'Schwaben',
        canonical: '/landing/webdesign-allgaeu',
        seoTitle: 'Webdesign Allgäu – Website & Betreuung für KMU | ToNetwork',
        seoDescription: 'Webdesign im Allgäu für KMU: moderne Website, Business-Fotos und monatliche Betreuung. ToNetwork aus Hawangen – All-in-One Digitalisierung mit transparenten Listenpreisen.',
        h1: 'Webdesign im Allgäu',
        h1sub: 'Digitalisierung aus einer Hand – für Unternehmen, die online durchstarten wollen.',
        localLead: 'Ob Memmingen, Kempten oder das ländliche Allgäu: Ein Ansprechpartner für Website, Hosting, Fotos und langfristige Betreuung.',
        areaServed: ['Allgäu', 'Memmingen', 'Kempten', 'Unterallgäu', 'Oberallgäu'],
    },
    kempten: {
        city: 'Kempten',
        region: 'Allgäu',
        canonical: '/landing/webdesign-kempten',
        seoTitle: 'Webdesign Kempten – Website für KMU ab 2.490 € | ToNetwork',
        seoDescription: 'Webdesign in Kempten und Umgebung: professionelle KMU-Website mit SEO, Business-Fotos und Care Plus Betreuung. ToNetwork – persönlicher Partner aus dem Allgäu.',
        h1: 'Webdesign in Kempten & Allgäu',
        h1sub: 'Professioneller Webauftritt für Unternehmen, die gefunden werden und Vertrauen aufbauen wollen.',
        localLead: 'Für KMU in Kempten und der Region: kurze Wege, direkte Kommunikation und Betreuung, die nicht nach Projektende aufhört.',
        areaServed: ['Kempten', 'Allgäu', 'Memmingen', 'Sonthofen', 'Immenstadt'],
    },
    default: {
        city: 'Memmingen',
        region: 'Allgäu',
        canonical: '/funnel/kmu',
        seoTitle: 'Webdesign für KMU – Website, Fotos & Betreuung | ToNetwork',
        seoDescription: 'KMU-Funnel von ToNetwork: Website (Business), Business-Foto-Tag und Care Plus Betreuung ab 2.490 €. Webdesign in Memmingen, Unterallgäu und Allgäu – transparente Listenpreise.',
        h1: 'Webdesign für KMU',
        h1sub: 'Website, Fotos & Betreuung – der Rundum-Start für Unternehmen, die online durchstarten wollen.',
        localLead: 'Aus Hawangen im Unterallgäu betreuen wir KMU in Memmingen, im Allgäu und darüber hinaus – mit festem Ansprechpartner und langfristiger Partnerschaft.',
        areaServed: ['Memmingen', 'Unterallgäu', 'Allgäu', 'Kempten'],
    },
}

function resolveRegion(pathname) {
    if (pathname.includes('memmingen')) return REGIONS.memmingen
    if (pathname.includes('unterallgaeu')) return REGIONS.unterallgaeu
    if (pathname.includes('allgaeu')) return REGIONS.allgaeu
    if (pathname.includes('kempten')) return REGIONS.kempten
    return REGIONS.default
}

const bundleKMU = {
    name: 'KMU-Wachstum',
    badge: '🔥 Beliebtes Bundle',
    featured: true,
    price: 'ab 2.490 €',
    priceExtra: '+ 99 € / Monat',
    contains: 'Website (Business) + Business-Foto-Tag + Care Plus',
    sales: 'Der Rundum-Start für Unternehmen, die online durchstarten wollen.',
    paket: 'bundle-kmu',
}

const webBusiness = {
    name: 'Business',
    badge: 'Einzeln buchbar',
    price: 'ab 2.190 €',
    priceNote: 'einmalig',
    forwho: 'KMU & Veranstalter',
    sales: 'Eure Website soll arbeiten, nicht nur hübsch sein. Individuell gestaltet, auf Google gefunden, gebaut, um Kunden zu gewinnen.',
    features: [
        { t: 'bis 6 Seiten', in: null },
        { t: 'Individuelles Design', in: null },
        { t: 'Mobiloptimiert', in: true },
        { t: 'SEO optimiert', in: true },
        { t: 'Kontaktformular', in: true },
        { t: 'Blog / News', in: true },
        { t: 'Shop / Buchung / Login', in: false },
        { t: '2 Revisionen · fertig in ~4 Wochen', in: null },
    ],
    paket: 'web-business',
}

const carePlus = {
    name: 'Care Plus',
    price: '99 €',
    priceNote: 'pro Monat',
    forwho: 'Der Allrounder',
    sales: 'Sicherheit plus regelmäßige Pflege und schnelle Reaktion – damit deine Seite dauerhaft Top-Form behält.',
    features: [
        { t: 'Hosting in Deutschland', in: true },
        { t: 'SSL & Sicherheits-Updates', in: true },
        { t: 'Monitoring (24/7) mit Benachrichtigungen', in: true },
        { t: 'Regelmäßige Backups', in: true },
        { t: '.de Domain inklusive', in: null },
        { t: '1 Std. Änderungen / Monat', in: true },
        { t: 'Reaktionszeit innerhalb von 2 Werktagen (7-20 Uhr)', in: null },
        { t: 'Fester Ansprechpartner', in: true },
        { t: 'Monatlich kündbar', in: true },
    ],
    paket: 'care-plus',
}

const painPoints = [
    {
        title: 'Die Website bringt keine Anfragen',
        text: 'Veraltetes Design, schlechte Google-Sichtbarkeit, kein Kontaktformular – potenzielle Kunden springen ab, bevor sie dich erreichen.',
    },
    {
        title: 'Niemand kümmert sich danach',
        text: 'Die meisten Agenturen bauen und verschwinden. Updates, Sicherheit und kleine Änderungen bleiben an dir hängen – oder werden teuer.',
    },
    {
        title: 'Alles aus verschiedenen Händen',
        text: 'Webdesigner, Fotograf, Hosting-Anbieter – vier Ansprechpartner, vier Rechnungen, kein roter Faden für deinen Auftritt.',
    },
]

const faqsForRegion = (city) => [
    {
        q: `Was kostet Webdesign für mein KMU in ${city}?`,
        a: 'Unser KMU-Bundle „Wachstum" startet ab 2.490 € einmalig plus 99 €/Monat für Care Plus Betreuung. Alle Preise sind Listenpreise ohne Rabatte – nach einem kostenlosen Erstgespräch erhältst du ein individuelles Festangebot.',
    },
    {
        q: `Betreut ihr auch Unternehmen außerhalb von ${city}?`,
        a: 'Ja. Wir sitzen in Hawangen bei Memmingen und arbeiten mit KMU im gesamten Unterallgäu, Allgäu und darüber hinaus – vor Ort oder per Video.',
    },
    {
        q: 'Bin ich an die monatliche Betreuung gebunden?',
        a: 'Nein. Care Plus ist monatlich kündbar. Wir wollen dich überzeugen, nicht festhalten.',
    },
    {
        q: 'Kann ich einzelne Leistungen statt des Bundles buchen?',
        a: 'Ja. Website (Business), Business-Foto-Tag und Care Plus sind auch einzeln buchbar. Das Bundle spart dir Koordinationsaufwand und ist unser meistgewähltes KMU-Paket.',
    },
]

function localBusinessJsonLd(region) {
    return {
        '@context': 'https://schema.org',
        '@type': 'ProfessionalService',
        name: 'ToNetwork UG (haftungsbeschränkt)',
        description: region.seoDescription,
        url: SITE,
        telephone: '+49-8332-4908872',
        email: 'hey@tonetwork.de',
        address: {
            '@type': 'PostalAddress',
            streetAddress: 'Hawangen',
            addressLocality: 'Hawangen',
            postalCode: '87749',
            addressRegion: 'Bayern',
            addressCountry: 'DE',
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 47.998,
            longitude: 10.283,
        },
        areaServed: region.areaServed.map((name) => ({ '@type': 'City', name })),
        priceRange: '€€',
        knowsAbout: ['Webdesign', 'Webentwicklung', 'Website-Betreuung', 'Fotografie', 'Hosting'],
    }
}

export default function FunnelKMU() {
    const { pathname } = useLocation()
    const region = resolveRegion(pathname)
    const canonicalUrl = SITE + region.canonical
    const faqs = faqsForRegion(region.city)

    const controls = useAnimationControls()

    useEffect(() => {
        controls.start('idle')
    }, [controls])

    useProgressiveHeroBg('sec_start--funnel-kmu')

    return (
        <div className="mainCon funnel_kmu" id="mainCon--funnel-kmu">

            <MainHeader funnel disableanimation={false} />

            <Helmet>
                <meta charSet="utf-8" />
                <title>{region.seoTitle}</title>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={region.seoDescription} />
                <meta name="keywords" content={`Webdesign ${region.city}, Website ${region.city}, Webentwicklung ${region.region}, KMU Website, ToNetwork`} />
                <meta property="og:title" content={region.seoTitle} />
                <meta property="og:description" content={region.seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="de_DE" />
                <script type="application/ld+json">{JSON.stringify(localBusinessJsonLd(region))}</script>
            </Helmet>

            <section className="sec_start progressive-hero-bg" id="sec_start--funnel-kmu">

                <motion.div
                    variants={boxvar1}
                    initial="hidden"
                    exit="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: '-50px' }}
                    className="start_heading_cont"
                >
                    <AnimatedQuote className="start_quote" text={region.h1} />

                    <p className="start_about">
                        {region.h1sub}<br />
                        <br />
                        {region.localLead}<br />
                        <br />
                        Unser Bundle: <b className="start_about_highlight">KMU-Wachstum</b> – Website, Business-Fotos & Care Plus ab 2.490 €
                    </p>
                </motion.div>

                <motion.div variants={boxvar3} initial="hidden" exit="hidden" whileInView="visible" viewport={{ once: false, margin: '-50px' }}>
                    <div className="sec_start_cta_row">
                        <CTAButton className="sec_start_btn" showArrow={false} customIcon={faPhone} iconLeft={true} title="Kostenloses Erstgespräch" onclick={() => { oPU(CALENDLY) }} />
                        <CTAButton className="sec_start_btn sec_start_btn-chat" showArrow={true} lightcta={true} title="Bundle anfragen" href="/support?paket=bundle-kmu" />
                    </div>
                </motion.div>

                <Link smooth to="#sec_next--funnel-kmu">
                    <motion.div
                        className="sec_next_icon_div noselect"
                        onHoverStart={() => controls.start('hover')}
                        onHoverEnd={() => controls.start('idle')}
                    >
                        <span>Erfahre mehr</span>
                        <motion.div variants={scrolldowniconanim} animate={controls}>
                            <FontAwesomeIcon icon={faChevronDown} className="sec_next_icon_arrow" />
                        </motion.div>
                    </motion.div>
                </Link>

            </section>

            <section className="sec_next" id="sec_next--funnel-kmu">
                <div className="sec_next_cont" id="sec_next--funnel-kmu--cont">

                    {/* Schmerzpunkte */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_kmu_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Kommt dir das bekannt vor?</h2>
                        <h3>Drei Probleme, die KMU in {region.city} und Umgebung kennen.</h3>
                        <div className="funnel_kmu_pain_grid">
                            {painPoints.map((p, i) => (
                                <motion.div
                                    key={p.title}
                                    variants={boxvar2dyndelay}
                                    custom={0.15 * (i + 1)}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: false, margin: '-50px' }}
                                    className="funnel_kmu_pain_card"
                                >
                                    <span className="funnel_kmu_pain_num">{String(i + 1).padStart(2, '0')}</span>
                                    <h4>{p.title}</h4>
                                    <p>{p.text}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Lösung: Bundle */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_kmu_section pricing_section"
                        id="funnel-kmu-angebot"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Die Lösung: KMU-Wachstum</h2>
                        <h3>Website, Business-Fotos und laufende Betreuung – aus einer Hand.</h3>
                        <p className="pricing_lead">
                            Statt vier Ansprechpartner bekommst du bei ToNetwork alles in einem Paket: eine individuelle Website,
                            die auf Google gefunden wird, professionelle Bilder für Team und Auftritt sowie monatliche Betreuung
                            mit festem Ansprechpartner – Hosting in Deutschland inklusive.
                        </p>
                        <div className="pricing_grid funnel_kmu_bundle_grid">
                            <BundleCard bundle={bundleKMU} delay={0.15} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf dein Projekt zugeschnitten. Stand: {STAND}
                        </p>
                    </motion.div>

                    {/* Einzelpakete */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_kmu_section pricing_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Oder einzeln buchen</h2>
                        <h3>Flexibel starten – jederzeit erweiterbar.</h3>
                        <div className="pricing_grid">
                            <PricingCard tier={webBusiness} delay={0.15} />
                            <PricingCard tier={carePlus} delay={0.3} />
                        </div>
                        <p className="pricing_note">
                            Alle Preise netto, zzgl. MwSt. – Listenpreise ohne Rabatte. Jedes Paket wird individuell auf dein Projekt zugeschnitten. Stand: {STAND}
                        </p>
                        <br />
                        <CTAButton
                            showArrow={false}
                            customIcon={faTags}
                            iconLeft={true}
                            title="Alle weiteren Pakete & Preise"
                            href="/pricing"
                        />
                    </motion.div>

                    {/* Warum ToNetwork + regional */}
                    <motion.div
                        variants={boxvar2_2}
                        className="funnel_kmu_section funnel_kmu_why"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="funnel_kmu_why_text">
                            <h2>Warum ToNetwork?</h2>
                            <h3>Weil du bei uns alles bekommst – aus einer Hand.</h3>
                            <p>
                                Wir sind kein anonymer Online-Dienstleister, sondern dein persönlicher Partner aus dem Allgäu.
                                Webentwicklung, Hosting, Fotografie und Betreuung unter einem Dach – mit direkter Kommunikation
                                und langfristiger Partnerschaft statt Projektabschluss ohne Support.
                            </p>
                            <ul className="funnel_kmu_benefits">
                                <li>Fester Ansprechpartner, der dich beim Namen kennt</li>
                                <li>Hosting in Deutschland – DSGVO-konform</li>
                                <li>Care Plus monatlich kündbar</li>
                                <li>Erstberatung kostenlos, Festangebot ohne versteckte Kosten</li>
                                <li>Skalierbar – du wechselst jederzeit das Paket</li>
                            </ul>
                            <CTAButton
                                showArrow={false}
                                customIcon={faMagnifyingGlassArrowRight}
                                iconLeft={true}
                                title="Alle Leistungen ansehen"
                                href="/services"
                            />
                        </div>
                        <img
                            src={macbook_webdev}
                            alt={`Webdesign und Webentwicklung für KMU in ${region.city}`}
                            className="funnel_kmu_why_img"
                            loading="lazy"
                        />
                    </motion.div>

                    {/* Testimonial */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_kmu_section"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Das sagen unsere Kunden</h2>
                        <div className="funnel_kmu_testimonial">
                            <span className="funnel_kmu_testimonial_quote">
                                „Mit ToNetwork haben wir einen langfristigen Partner für den Bereich Webentwicklung gefunden."
                            </span>
                            <div className="funnel_kmu_testimonial_person">
                                <img src={testimonial_klaus} alt="Klaus Liepert, CEO it.liepert.gmbh" />
                                <div>
                                    <span className="funnel_kmu_testimonial_name">Klaus Liepert</span>
                                    <span className="funnel_kmu_testimonial_company">CEO, it.liepert.gmbh</span>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* FAQ */}
                    <motion.div
                        variants={boxvar2_1}
                        className="funnel_kmu_section pricing_section"
                        id="funnel-kmu-faq"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <h2>Häufige Fragen</h2>
                        <div className="pricing_faq_list">
                            {faqs.map((f, i) => (
                                <details key={i} className="pricing_faq_item" open={i === 0}>
                                    <summary>{f.q}</summary>
                                    <p>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </motion.div>

                    {/* Abschluss-CTA */}
                    <motion.div
                        variants={boxvar2dyndelay}
                        className="small_services_box small_services_box-bigbottom funnel_kmu_cta_box"
                        initial="hidden"
                        whileInView="visible"
                        custom={0.3}
                        viewport={{ once: false, margin: '-50px' }}
                    >
                        <div className="small_services_textbox">
                            <h3>Bereit für {region.city}? Lass uns reden.</h3>
                            <br />
                            <p>
                                In einem kostenlosen 30-Minuten-Gespräch finden wir heraus, was dein Unternehmen wirklich
                                braucht – ehrlich und ohne Verkaufsdruck. Alle Preise hier sind Listenpreise ohne Rabatte
                                und werden individuell für dich ausgestaltet.
                            </p>
                            <CTAButton
                                showArrow={false}
                                customIcon={faPhone}
                                iconLeft={true}
                                title="Kostenloses Erstgespräch"
                                onclick={() => { oPU(CALENDLY) }}
                            />
                            {/* <br /> */}
                            {/* <CTAButton
                                className="funnel_kmu_cta_secondary"
                                showArrow={true}
                                title="Bundle KMU-Wachstum anfragen"
                                href="/support?paket=bundle-kmu"
                            /> */}
                            <br />
                            <p className="pricing_toni_hint">
                                Erst Orientierung gefragt? <a href="/chat">ToNi beantwortet Fragen zu Paketen und Preisen</a> – unverbindlich im Chat.
                            </p>
                        </div>
                    </motion.div>

                    <p className="pricing_footnote">
                        Es gelten unsere <a href="/rechtliches/agb">Allgemeinen Geschäftsbedingungen (AGB)</a>
                    </p>

                    <br /><br /><br />
                </div>
            </section>

            <MainFooter />
        </div>
    )
}
